import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { useStoreState } from '../../../model/hooks'
import Spinner from '../../Spinner'
import BigNumber from 'bignumber.js'
import { calculateRatioFromTickForTokens, findClosestTick, getFeeMultiplierDivider, normalizeForTokens, numFormat } from '../../../blockchainUtils/math'
import RangeSliderSelector from '../RangeSliderSelector/RangeSliderSelector'
import RangeSelector from '../RangeSelector/RangeSelector'
import { useWalletContext } from '../../WalletContext'
import { CLPool, Token } from '../../../atoms'
import { HelptipIcon } from '../../../svgIcons/HelptipIcon'
import { useDebounce } from 'usehooks-ts'
import axios from 'axios'
import { apiUrl } from '../../../blockchainUtils'
import { loadPoolOverviewData } from '../../charts/chartsApi'
import { MAX_TICK } from '../helper'
// import { tokenAddresses } from '../../IncentivePage/IncentivePage'

function RangeStep(props: {
    renderActionButton: () => JSX.Element
    setCustomRange: Dispatch<SetStateAction<number>>
    setMaxTick: (tick: number) => void

    // setMaxTick: Dispatch<SetStateAction<number>>
    setIsRatioReversed: Dispatch<SetStateAction<boolean>>
    // setMinTick: Dispatch<SetStateAction<number>>
    setMinTick: (tick: number) => void
    setRangeSelected: (range: number) => void
    setIsMaxRangSelected: Dispatch<SetStateAction<boolean>>
    customRange: number
    token1: string
    token2: string
    rangeOptions: number[]
    stepNumber: number
    updateMinTickBasedOnPrice: (addNumber: string) => void
    updateMaxTickBasedOnPrice: (addNumber: string) => void
    updateMaxTick: (addNumber: number) => void
    updateMinTick: (addNumber: number) => void
    maxTick: number
    minTick: number
    currentTick: number
    currentRatio: number
    fee: number
    rangeSelected: number
    tickAmount: 1 | 2 | 10 | 60 | 200
    isMaxRangeSelected: boolean
    isLoadingTicks: boolean
    isRatioReversed: boolean
    selectedPool: CLPool | undefined
    isCreatePool?: boolean
    onUpdateCurrentRatio?: (ratio: number) => void
}) {
    const {
        renderActionButton,
        setCustomRange,
        customRange,
        token1,
        token2,
        rangeOptions,
        stepNumber,
        updateMaxTick,
        updateMinTick,
        maxTick,
        minTick,
        currentTick,
        fee,
        rangeSelected,
        isMaxRangeSelected,
        setRangeSelected,
        setIsMaxRangSelected,
        isLoadingTicks,
        isRatioReversed,
        currentRatio,
        tickAmount,
        setMaxTick,
        setMinTick,
        setIsRatioReversed,
        selectedPool,
        isCreatePool = false,
        onUpdateCurrentRatio,
        updateMaxTickBasedOnPrice,
        updateMinTickBasedOnPrice
    } = props
    const { networkName: network } = useWalletContext()

    const getToken = useStoreState((state) => state.tokens.getToken)
    const [initialPrice, setInitialPrice] = useState('')
    const [projectedAPR, setProkectedAPR] = useState(0)
    const [isCalculatingApr, setIsCalculatingApr] = useState(false)
    const debouncedInitialPrice = useDebounce(initialPrice, 1000)
    const debouncedMinTick = useDebounce(minTick, 1000)
    const debouncedMaxTick = useDebounce(maxTick, 1000)
    const [incentiveAPR, setInceintveAPR] = useState(0)

    useEffect(() => {
        if (onUpdateCurrentRatio && debouncedInitialPrice) {
            onUpdateCurrentRatio(Number(debouncedInitialPrice))
        }
    }, [debouncedInitialPrice])

    useEffect(() => {
        if (selectedPool && selectedPool.address) loadPoolOVerview(selectedPool.address)
    }, [selectedPool])

    useEffect(() => {
        if (selectedPool && selectedPool.address) loadAPRInRange()
    }, [debouncedMinTick, debouncedMaxTick])

    async function loadPoolOVerview(poolKey: string) {
        const response = await loadPoolOverviewData(poolKey)

        setInceintveAPR(response && response?.apr && response?.apr.incentive_apr ? response?.apr.incentive_apr : 0)
    }

    async function loadAPRInRange() {
        if (debouncedMaxTick === 0 && debouncedMinTick == 0) return
        if (!selectedPool || isCreatePool) return
        try {
            setIsCalculatingApr(true)
            const response = await axios.get(
                `${apiUrl(network ?? '')}/cl/apr?pkey=${selectedPool.address}&range_low=${debouncedMinTick + MAX_TICK}&range_high=${debouncedMaxTick + MAX_TICK}`
            )
            setIsCalculatingApr(false)
            setProkectedAPR(response.data.apr * 100)
        } catch (e) {
            setIsCalculatingApr(false)
        }
    }

    function renderRangeErrorMessage() {
        if (isCreatePool) return null
        const isError = (maxTick > currentTick && minTick > currentTick) || (maxTick < currentTick && minTick < currentTick)
        if (!isError) return null
        return (
            <div className='rounded-lg mt-4 p-4 text-[16px] font-bold bg-[#391F45] text-[#FF5353]'>
                Your position will not earn fees or be used in trades until the market price moves into your range
            </div>
        )
    }

    function renderRangeSelectorOption(option: number) {
        const isSelected = rangeSelected === option && !isMaxRangeSelected

        return (
            <span
                key={option}
                onClick={() => {
                    setRangeSelected(option)
                    setCustomRange(undefined as unknown as number)
                    setIsMaxRangSelected(false)
                }}
                className={`flex-1 select-none h-[24px] items-center flex text-[11px] mr-2 cursor-pointer rounded-[8px] border-[1px] border-[${
                    isSelected ? '#0AE8F9' : '#0C3F6D'
                }] justify-center`}>
                {`±${option}%`}
            </span>
        )
    }

    function renderCustomRange() {
        const isSelected = Boolean(customRange)

        return (
            <div className={`flex-1  relative`}>
                {/* <div className={`flex-1  relative min-w-[60px] w-[60px] max-w-[60px]`}> */}
                <input
                    value={customRange || ''}
                    placeholder='Custom'
                    min={0}
                    // max={50}
                    className={`h-[24px] w-full items-center flex outline-none bg-transparent rounded-[8px] text-[11px] border-[1px] border-[${
                        isSelected ? '#0AE8F9' : '#0C3F6D'
                    }] px-1 shrink-0  focus:border-[#0AE8F9] flex cursor-pointer justify-center text-center pl-4 pr-4`}
                    onChange={(e) => {
                        if (e.target.value.length > 14) return
                        setCustomRange(Number(e.target.value))
                        setRangeSelected(undefined as unknown as number)
                        setIsMaxRangSelected(false)
                    }}
                    type='number'
                />
                {customRange ? <span className='text-[11px] absolute top-[4px] right-[6px]'>%</span> : null}
            </div>
        )
    }

    function renderStepRangeSelector() {
        // const ranges = [5, 10, 20, 30]
        return (
            <div className='flex items-center mb-[14px] mt-[10px] w-full'>
                {rangeOptions.map(renderRangeSelectorOption)}
                {/*renderRangeSelectorOptionMax()*/}
                {renderCustomRange()}
            </div>
        )
    }

    function renderStartPrice() {
        if (!isCreatePool) return null
        const token1Obj = getToken(network!, token1)
        const token2Obj = getToken(network!, token2)
        const isTokensSameOrder = BigNumber(token2).gt(token1)
        // const isTokensSameOrder = selectedPool && selectedPool.token1 && token1.toLowerCase() === selectedPool.token1.address
        const tok1 = isTokensSameOrder ? token1Obj : token2Obj
        const tok2 = isTokensSameOrder ? token2Obj : token1Obj

        return (
            <div className='flex flex-col mt-[18px]'>
                <span className='text-white font-bold text-[18px]'>Start Price</span>
                <span className='text-[#6D7391] text-[14px] mt-2'>To add liquidity, first initialize the pool by selecting a starting price</span>
                <div className='h-[46px] bg-[#1D285A] flex items-center rounded-[16px] py-2 px-[8px] mt-[10px]'>
                    <input
                        placeholder='Enter the ratio between the tokens'
                        type='number'
                        onChange={(e) => setInitialPrice(e.target.value)}
                        value={initialPrice}
                        className={`p-2 rounded-xl bg-transparent w-full h-full outline-none text-white ${initialPrice ? 'font-bold text-[18px]' : 'text-[12px]'}`}
                    />
                    <span className='flex-shrink-0 text-[#6F6E8F] text-[12px]'>{`${tok1?.symbol} Per ${tok2?.symbol}`}</span>
                </div>
            </div>
        )
    }

    function renderRangeSelectors(ratioReversed: boolean, isReversed: boolean, _maxVal: BigNumber, _minVal: BigNumber, tok1: Token, tok2: Token) {
        return (
            <div className='flex w-full justify-between lg:flex-row flex-col items-center lg:items-auto'>
                {/* <div className='mt-2 flex w-full justify-between'> */}
                <RangeSelector
                    isDisabled={isMaxRangeSelected}
                    setIsOtherRatio={() => setIsRatioReversed(!isRatioReversed)}
                    isOtherRatio={ratioReversed}
                    pricePerTokenB={isReversed ? _maxVal : _minVal}
                    isMax={false}
                    isReversed={isReversed}
                    tokenBSymbol={tok1!.symbol}
                    tokenASymbol={tok2!.symbol}
                    ticksChange={tickAmount}
                    network={network!}
                    onUpdateCurrentTick={isRatioReversed ? updateMinTick : updateMaxTick}
                    onUpdateCurrentTickByPrice={isRatioReversed ? updateMinTickBasedOnPrice : updateMaxTickBasedOnPrice}
                />
                <RangeSelector
                    isDisabled={isMaxRangeSelected}
                    isOtherRatio={ratioReversed}
                    setIsOtherRatio={() => setIsRatioReversed(!isRatioReversed)}
                    pricePerTokenB={isReversed ? _minVal : _maxVal}
                    isMax={true}
                    isReversed={isReversed}
                    className='lg:ml-2 mt-2 lg:mt-0'
                    tokenBSymbol={tok1!.symbol}
                    tokenASymbol={tok2!.symbol}
                    ticksChange={tickAmount}
                    network={network!}
                    onUpdateCurrentTick={isRatioReversed ? updateMaxTick : updateMinTick}
                    onUpdateCurrentTickByPrice={isRatioReversed ? updateMaxTickBasedOnPrice : updateMinTickBasedOnPrice}
                    // onUpdateCurrentTick={isReversed ? updateMinTick : updateMaxTick}
                />
            </div>
        )
    }

    function renderRangeSelection() {
        const token1Obj = getToken(network!, token1)
        const token2Obj = getToken(network!, token2)
        const isTokensSameOrder = selectedPool && selectedPool.token1 && token1.toLowerCase() === selectedPool.token1.address
        const tok1 = isTokensSameOrder ? token1Obj : token2Obj
        const tok2 = isTokensSameOrder ? token2Obj : token1Obj
        if (!tok1 || !tok2) return null
        let isReversed = false
        let _minVal = BigNumber(1).dividedBy(calculateRatioFromTickForTokens(minTick, tok1!, tok2!))
        let _maxVal = BigNumber(1).dividedBy(calculateRatioFromTickForTokens(maxTick, tok1!, tok2!))

        if (isMaxRangeSelected) {
            _minVal = BigNumber(Infinity)
            _maxVal = BigNumber(0)
        }

        // const isReversedHigher = 1 / Number(calculateRatioFromTickForTokens(minTick, tok1!, tok2!)) < Number(calculateRatioFromTickForTokens(minTick, tok1!, tok2!))
        let ratioReversed = isRatioReversed
        // let ratioReversed = isReversedHigher ? !isRatioReversed : isRatioReversed
        isReversed = !ratioReversed

        const { maxMultiplier, minDivider } = getFeeMultiplierDivider(fee)
        let _currentPrice = normalizeForTokens(BigNumber(currentRatio), tok1!, tok2!)
        if (isReversed) _currentPrice = BigNumber(1).dividedBy(_currentPrice)
        const currentPrice = numFormat(_currentPrice.toString())
        if (isCreatePool) return null

        return (
            <>
                <RangeSliderSelector
                    rangeSelected={rangeSelected}
                    poolId={selectedPool ? selectedPool.address : ''}
                    currentPrice={Number(currentPrice)}
                    maxMultiplier={maxMultiplier}
                    minDivider={minDivider}
                    isReversed={isReversed}
                    currentRatio={currentRatio}
                    tickAmount={tickAmount}
                    onUpdateMaxTick={(number) => {
                        // setRangeSelected(undefined as unknown as number)
                        setCustomRange(undefined as unknown as number)
                        const closestTick = findClosestTick(number, tickAmount)
                        setMaxTick(closestTick)
                    }}
                    onUpdateMinTick={(number) => {
                        // setRangeSelected(undefined as unknown as number)
                        setCustomRange(undefined as unknown as number)
                        const closestTick = findClosestTick(number, tickAmount)
                        setMinTick(closestTick)
                    }}
                    maxTick={maxTick}
                    minTick={minTick}
                    currentTick={currentTick}
                />
                {renderRangeSelectors(ratioReversed, isReversed, _maxVal, _minVal, tok1!, tok2!)}
            </>
        )
    }

    function renderProjectedAPRPerRange() {
        if (isCreatePool || network !== 'mainnet-alpha') return null
        // if (token1 === tokenAddresses.STRK || token2 === tokenAddresses.STRK) return <div className='w-full h-[24px]'></div> // POST-STARK TODO REMOVE

        return (
            <div className='flex relative items-center mt-3 mb-3'>
                <div className='ml-2 group inline-flex'>
                    <HelptipIcon className='w-4 h-4 text-white mr-1' />
                    {/* <span className="bg-red-400 text-white px-2 py-1">Button</span> */}
                    <span className='left-[auto] lg:left-[-20px] bottom-[24px] w-[200px] group-hover:visible invisible group-hover:opacity-100 transition-opacity bg-[#242C58] text-sm text-gray-100 rounded-md absolute opacity-0  p-3 z-[1]'>
                        {`Estimate the Annualized Percentage Revenue (APR) by annualizing daily fees and dividing by the TVL in a selected range.`}
                        {/* {'7-day moving average'} */}
                    </span>
                </div>
                {/* <HelptipIcon className='w-4 h-4 text-white mr-1' /> */}
                <span className='text-white text-[12px]'>Projected APR:</span>
                <span className='text-white text-[10px] opacity-[0.4] ml-2'>(last 24h):</span>
                <span className='ml-2 bg-[#0C3F6D] text-[#0AE8F9] text-[12px] px-1 rounded-[8px] py-1 h-[24px]'>
                    {isCalculatingApr ? null : `${projectedAPR > 500 ? '>500' : numFormat((projectedAPR + incentiveAPR).toFixed(2))}%`}
                    {isCalculatingApr ? <Spinner className='mr-0 text-[#0AE8F9]' height='16px' width='16px' /> : null}
                </span>
                {incentiveAPR ? <span className='ml-2 text-[12px]' style={{ color: 'rgba(255, 255, 255, 0.50)' }}>{`(${incentiveAPR.toFixed(2)}% DeFi Incentives)`}</span> : null}
            </div>
        )
    }

    function renderStepRange() {
        if (stepNumber < 3) return null
        if (!selectedPool && !isCreatePool) return null
        if (isLoadingTicks && !isCreatePool)
            return (
                <div className='mt-4'>
                    <Spinner />
                </div>
            )

        return (
            <div className='flex flex-col w-full'>
                <div className='flex items-start mb-4 mt-4'>
                    <div className='flex flex-col w-full'>
                        <div className='flex items-center'>
                            {renderStepIdentifier(2, false)}
                            <span className='ml-4 text-[18px] text-white font-bold'>Set Price Range</span>
                        </div>
                        {renderStartPrice()}

                        {renderStepRangeSelector()}
                        {renderProjectedAPRPerRange()}
                    </div>
                </div>
                {renderRangeSelection()}
                {renderRangeErrorMessage()}
                {stepNumber === 4 ? null : renderActionButton()}
            </div>
        )
    }

    function renderStepIdentifier(number: number, isDisabled = false) {
        return (
            <div
                className={`${
                    isDisabled ? 'bg-[#366FB7] text-[#272756]' : 'bg-[#0AE8F9] text-[#272756]'
                }  rounded-sm   text-[18px] p-1 w-[20px] h-[20px] flex items-center justify-center`}>
                {number}
            </div>
        )
    }

    return renderStepRange()
}

export default RangeStep
